import { BatteryLow, CircleCheck, OctagonX, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { api, type Slo } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { Meter } from '../../components/Meter';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatPercent } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { budgetRemaining, budgetTone, ratio } from './metrics';
import { RefreshButton } from './shared';

/** `/ops/slo`: versioned SLO definitions with the current SLI (spec §119). */
export function SloPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('slo.title'));
  const result = useApi((signal) => api.listSlos({ signal }), []);
  const items = result.status === 'success' ? result.data.items : [];
  const remaining = items.map(budgetRemaining);
  const known = remaining.filter((r): r is number => r !== null);

  const columns: Column<Slo>[] = [
    {
      key: 'slo',
      header: t('slo.col.slo'),
      cell: (s) => (
        <div className="min-w-48">
          <div className="font-medium">{s.name ?? s.id}</div>
          <div className="font-mono text-xs text-muted-foreground">
            {s.id} · v{String(s.version ?? '?')}
          </div>
        </div>
      ),
    },
    {
      key: 'service',
      header: t('slo.col.service'),
      cell: (s) => (s.service ? <EntityRef label={s.service} to={`/ops/services/${encodeURIComponent(s.service)}`} /> : '—'),
    },
    { key: 'objective', header: t('slo.col.objective'), numeric: true, cell: (s) => formatPercent(ratio(s.objective), 2) },
    {
      key: 'current',
      header: t('slo.col.current'),
      numeric: true,
      cell: (s) => {
        const objective = ratio(s.objective);
        const current = ratio(s.current ?? null);
        if (current !== null) return <Badge tone={objective !== null && current >= objective ? 'ok' : 'serious'}>{formatPercent(current, 3)}</Badge>;
        if (s.current_error)
          return (
            <span className="text-xs font-medium text-critical" title={s.current_error}>
              {t('slo.queryFailed')}
            </span>
          );
        return <span className="text-xs text-muted-foreground">{t('slo.noBackend')}</span>;
      },
    },
    {
      key: 'budget',
      header: t('slo.col.budget'),
      cell: (s) => {
        const r = budgetRemaining(s);
        if (r === null) return <span className="text-muted-foreground">—</span>;
        const tone = budgetTone(r);
        return (
          <div className="flex min-w-44 items-center gap-3">
            <div className="flex-1">
              <Meter value={Math.max(0, r)} tone={tone} label={t('slo.meterLabel', { name: s.name ?? s.id ?? 'SLO' })} />
            </div>
            <span className={cn('tabular w-20 text-right text-sm font-semibold', tone === 'crit' && 'text-critical', tone === 'warn' && 'text-warning')}>
              {r <= 0 ? t('slo.exhausted') : formatPercent(r, 0)}
            </span>
          </div>
        );
      },
    },
    { key: 'window', header: t('slo.col.window'), cell: (s) => s.window ?? '—' },
  ];

  return (
    <>
      <PageHeader title={t('slo.title')} subtitle={t('slo.subtitle')} actions={<RefreshButton onClick={result.reload} busy={result.status === 'loading'} />} />
      {result.status === 'success' && items.length ? (
        <StatGrid className="mb-6">
          <StatCard icon={Target} label={t('slo.stat.total')} value={formatCount(items.length)} />
          <StatCard icon={CircleCheck} label={t('slo.stat.meeting')} value={`${known.filter((r) => r > 0).length}/${known.length}`} tone="ok" />
          <StatCard icon={BatteryLow} label={t('slo.stat.low')} value={formatCount(known.filter((r) => r > 0 && r <= 0.25).length)} tone={known.some((r) => r > 0 && r <= 0.25) ? 'warn' : 'neutral'} hint={t('slo.stat.lowHint')} />
          <StatCard icon={OctagonX} label={t('slo.stat.exhausted')} value={formatCount(known.filter((r) => r <= 0).length)} tone={known.some((r) => r <= 0) ? 'crit' : 'neutral'} hint={t('slo.stat.exhaustedHint')} />
        </StatGrid>
      ) : null}
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('slo.loadFailed')} />
          </div>
        ) : null}
        {result.status === 'success' ? <DataTable columns={columns} rows={items} rowKey={(s) => s.id ?? s.name ?? ''} empty={<EmptyState title={t('slo.empty')} />} /> : null}
      </Section>
      <Callout className="mt-6" title={t('slo.policyTitle')}>
        {t('slo.policy')}
      </Callout>
    </>
  );
}
